import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { SectionLabel } from "@/components/site/SectionLabel";
import { PageMeta } from "@/components/site/PageMeta";
import { getSection, listItems } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Briefcase, GraduationCap } from "lucide-react";

const CHAPTERS = [
    { id: "summary", label: "Summary" },
    { id: "qualifications", label: "Qualifications" },
    { id: "journey", label: "Academic Journey" },
    { id: "timeline", label: "Positions" },
    { id: "industry", label: "Industry Exposure" },
    { id: "awards", label: "Awards" },
    { id: "volunteering", label: "Volunteering" },
    { id: "extracurricular", label: "Extracurricular" },
];

// Classify an entry as academic vs industry so the timeline nodes can be styled
function isAcademic(entry) {
    const s = `${entry.title || ""} ${entry.org || ""}`.toLowerCase();
    return (
        s.includes("fellow") ||
        s.includes("professor") ||
        s.includes("faculty") ||
        s.includes("lecturer") ||
        s.includes("nlu") ||
        s.includes("university") ||
        s.includes("assistant")
    );
}

export default function AboutPage() {
    const [about, setAbout] = useState(null);
    const [home, setHome] = useState(null);
    const [cv, setCv] = useState(null);
    const [awards, setAwards] = useState([]);
    const [active, setActive] = useState("summary");

    useEffect(() => {
        getSection("about").then((r) => setAbout(r.data || {}));
        getSection("home").then((r) => setHome(r.data || {}));
        getSection("cv").then((r) => setCv(r.data || {}));
        listItems("awards").then(setAwards);
    }, []);

    useEffect(() => {
        const els = CHAPTERS.map((c) => document.getElementById(c.id));
        const obs = new IntersectionObserver(
            (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }); },
            { rootMargin: "-45% 0px -45% 0px" },
        );
        els.forEach((el) => el && obs.observe(el));
        return () => obs.disconnect();
    }, []);

    const paragraphs = useMemo(() => (about?.summary || "").split(/\n\n+/), [about]);
    const qualifications = home?.qualifications || ["LL.M. (Gold Medalist)", "LL.B.", "CS", "B.Com", "UGC NET (JRF)"];
    const timeline = useMemo(() => (cv?.work_experience || []), [cv]);

    return (
        <>
            <PageMeta title="About — Aditya Baheti" description="Legal academic, researcher, and Company Secretary. Corporate law, securities regulation, corporate governance." path="/about" />

            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-6">
                <SectionLabel label="About" />
                <Reveal>
                    <h1 className="font-display text-4xl sm:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                        A career at the seam of practice and scholarship.
                    </h1>
                </Reveal>
            </section>

            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 grid lg:grid-cols-12 gap-10">
                <aside className="hidden lg:block lg:col-span-3">
                    <nav className="sticky top-24" aria-label="Section navigation">
                        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Chapters</p>
                        <ul className="space-y-2 text-sm">
                            {CHAPTERS.map((c) => (
                                <li key={c.id}>
                                    <a href={`#${c.id}`} className={`inline-flex items-center gap-2 transition-colors ${active === c.id ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`about-toc-${c.id}`}>
                                        <motion.span layoutId="about-toc-dot" style={{ opacity: active === c.id ? 1 : 0 }} className="h-1.5 w-1.5 rounded-full bg-foreground" />
                                        {c.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </aside>

                <div className="lg:col-span-9 space-y-16">
                    <article id="summary">
                        <SectionLabel label="Summary" />
                        <Reveal>
                            <div className="prose max-w-[68ch] space-y-4">
                                {paragraphs.map((p, i) => (
                                    <p key={i} className={i === 0 ? "first-letter:font-display first-letter:text-5xl first-letter:float-left first-letter:mr-2 first-letter:leading-[0.9] text-base sm:text-lg leading-relaxed" : "text-base sm:text-lg leading-relaxed"}>{p}</p>
                                ))}
                            </div>
                        </Reveal>
                    </article>

                    <article id="qualifications">
                        <SectionLabel label="Qualifications" />
                        <Reveal>
                            <div className="flex flex-wrap gap-2" data-testid="about-qualifications-block">
                                {qualifications.map((q) => (
                                    <Badge key={q} variant="outline" className="rounded-full border-border/70 bg-card font-mono text-[12px] tracking-wide px-3 py-1.5">{q}</Badge>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <p className="mt-4 text-sm text-muted-foreground max-w-[62ch]">A postgraduate in law, qualified Company Secretary, and UGC NET (JRF) qualified scholar.</p>
                        </Reveal>
                    </article>

                    <article id="journey">
                        <SectionLabel label="Academic Journey" />
                        <Reveal>
                            <p className="max-w-[68ch] text-base sm:text-lg leading-relaxed text-muted-foreground">{about?.academic_journey}</p>
                        </Reveal>
                    </article>

                    {/* ============ VERTICAL TIMELINE ============ */}
                    <article id="timeline" className="scroll-mt-24">
                        <SectionLabel label="Positions" />
                        <Reveal>
                            <h2 className="font-display text-3xl sm:text-4xl tracking-tight max-w-3xl">
                                Appointments & professional roles.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <p className="mt-3 max-w-[62ch] text-muted-foreground">
                                A vertical timeline of academic appointments and industry roles — newest first.
                            </p>
                        </Reveal>

                        {timeline.length > 0 ? (
                            <ol
                                className="mt-10 relative border-l-2 border-border/70 pl-8 space-y-10"
                                data-testid="about-timeline"
                            >
                                {timeline.map((entry, i) => {
                                    const academic = isAcademic(entry);
                                    return (
                                        <Reveal key={i} delay={i * 0.05} as="li" className="relative">
                                            {/* Node marker */}
                                            <span
                                                aria-hidden
                                                className={`absolute -left-[38px] top-1 h-6 w-6 rounded-full border-2 grid place-items-center ${
                                                    academic
                                                        ? "bg-forest/10 border-forest"
                                                        : "bg-background border-brass"
                                                }`}
                                            >
                                                {academic ? (
                                                    <GraduationCap className="h-3 w-3 text-forest" />
                                                ) : (
                                                    <Briefcase className="h-3 w-3 text-brass" />
                                                )}
                                            </span>
                                            {/* Content */}
                                            <div>
                                                {entry.period && (
                                                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brass">
                                                        {entry.period}
                                                    </p>
                                                )}
                                                <p className="mt-1 font-display text-2xl leading-snug">
                                                    {entry.title}
                                                </p>
                                                {entry.org && (
                                                    <p className="mt-1 text-sm font-mono uppercase tracking-widest text-muted-foreground">
                                                        {entry.org}
                                                    </p>
                                                )}
                                                {entry.body && (
                                                    <p className="mt-3 text-base text-muted-foreground max-w-[68ch] leading-relaxed">
                                                        {entry.body}
                                                    </p>
                                                )}
                                            </div>
                                        </Reveal>
                                    );
                                })}
                            </ol>
                        ) : (
                            <p className="mt-6 text-sm text-muted-foreground">Timeline coming soon.</p>
                        )}
                    </article>

                    <article id="industry">
                        <SectionLabel label="Industry Exposure" />
                        <Reveal>
                            <p className="max-w-[68ch] text-base sm:text-lg leading-relaxed text-muted-foreground">{about?.industry_exposure}</p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {(about?.industry_stints || []).map((s) => (
                                    <Badge key={s} variant="outline" className="rounded-full border-border/70 bg-card font-mono text-[11px]">{s}</Badge>
                                ))}
                            </div>
                        </Reveal>
                    </article>

                    <article id="awards">
                        <SectionLabel label="Awards & Achievements" />
                        <div className="grid md:grid-cols-2 gap-4 mt-4">
                            {awards.map((a, i) => (
                                <Reveal key={a.id} delay={i * 0.06}>
                                    <Card className="p-5 rounded-2xl border-border/70">
                                        <p className="font-display text-xl leading-snug">{a.title}</p>
                                        {a.issuer && <p className="mt-1 font-mono text-xs text-muted-foreground uppercase tracking-widest">{a.issuer}{a.year ? ` · ${a.year}` : ""}</p>}
                                        {a.description && <p className="mt-3 text-sm text-muted-foreground">{a.description}</p>}
                                    </Card>
                                </Reveal>
                            ))}
                        </div>
                    </article>

                    <article id="volunteering">
                        <SectionLabel label="Volunteering & Outreach" />
                        <Reveal><p className="max-w-[68ch] text-base sm:text-lg leading-relaxed text-muted-foreground">{about?.volunteering_intro}</p></Reveal>
                        <div className="mt-6 space-y-6">
                            {(about?.volunteering || []).map((v, i) => (
                                <Reveal key={i} delay={i * 0.06}>
                                    <div className="border-l-2 border-foreground/70 pl-5">
                                        <p className="font-display text-xl leading-snug">{v.title}</p>
                                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.body}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </article>

                    <article id="extracurricular">
                        <SectionLabel label="Extracurricular" />
                        <Reveal>
                            <p className="max-w-[68ch] text-base sm:text-lg leading-relaxed text-muted-foreground">{about?.extracurricular}</p>
                        </Reveal>
                    </article>
                </div>
            </section>
        </>
    );
}
